import React, {lazy, Suspense, useContext, useEffect, useState} from "react";
import {Redirect} from "react-router-dom";
import * as ROUTES from "../../../common/routes";
import {PROFILE_ACCOUNT_SETTINGS_PERSONAL_DETAILS} from "../../../common/routes";
import * as CONSTANTS from "../../../common/constant";
import {
    CUSTOMER,
    CUSTOMER_EMPLOYEE,
    CUSTOMER_MEMBER,
    CUSTOMER_TRIAL,
    KEYCLOAK_SESSION_EXPIRATION,
    SHARE_MARKETPLACE_USER,
    CHAT_GUURU_APP_ID
} from "../../../common/constant";
import makeStyles from "@material-ui/core/styles/makeStyles";
import LocalizedSwitch from "../../app/routing/LocalizedSwitch";
import {getCachedData, contentful} from "../../../common/apollo";
import {QUERY as ComposePagesQuery} from "../../../hooks/useComposePages";
import {useKeycloakFunctions} from "../../../hooks/useKeycloakFunctions";
import {useLocalStorage, useMaintenanceWindow} from "../../../hooks";
import {useWindowFocus} from "../../../hooks/useWindowFocus";
import {getBrowser, getQueryVariable, useI18N, useLocation} from "../../../common/common";
import * as PropTypes from "prop-types";
import Route from "../../app/routing/Route";
import PrivateRoute from "../../app/routing/PrivateRoute";
import IdleDialog from "../../dialog/idleDialog/IdleDialog";
import NewsletterDialog from "../../dialog/newsletter/NewsletterDialog";
import {useCookies} from "react-cookie";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {LocalizedContext} from "../../../store/providers/LocalizedProvider";
import {SUPPORTED_LANGUAGE} from "../../../common/i18n/locales/localeConstant";
import AccountSettingsView from "../../view/profile/AccountSettingsView";
import LocalizedRedirect from "../../app/routing/LocalizedRedirect";
import PersonalDetailsView from "../../view/profile/PersonalDetailsView";
import DynamicRouter from "../../app/routing/DynamicRouter";
import {UserContext} from "../../../store/providers/UserProvider";
import {
    checkMaintenancePage,
    checkYearEndProcessing,
    disableURLForOnlineAccess,
    checkAvailabilityOverviewPageDisabled,
    checkDocumentsForTrialsAndProspectsDisabled,
} from "../../../common/configcat/functions";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import InAppBrowserDialog from "../../dialog/InAppBrowserDialog";
import {RouteContext} from "../../../store/providers/RouteProvider";
import ApartmentCategoryAvailabilityCalendarProvider from "../../../store/providers/ApartmentCategoryAvailabilityCalendarProvider";
import AvailabilityOverviewProvider from "../../../store/providers/AvailabilityOverviewProvider";

/*
* Lazy Loading route components
*/
const Home = lazy(() => import("../../view/home/Home"));
const OnlineAccessView = lazy(() => import("../../view/online-access/OnlineAccessView"));
// const EmailVerificationOnlineAccess = lazy(() => import("../../view/email-verification/EmailVerificationOnlineAccess"));
const AdminDialog = lazy(() => import("../../view/admin/AdminDialog"));
const LegalPages = lazy(() => import("../../view/legal-pages/LegalPages"));
const BecomeMemberView = lazy(() => import("../../view/become-member/BecomeMemberView"));
const EmailVerificationView = lazy(() => import("../../view/email-verification/EmailVerificationView"));
const Versions = lazy(() => import("../../view/versions/Versions"));
const ButtonsAndChips = lazy(() => import("../../view/buttons/ButtonsAndChips"));
const Intl = lazy(() => import("../../view/intl/Intl"));
const ResortsView = lazy(() => import("../../view/searchbar/ResortsView"));
const MarketPlaceView = lazy(() => import("../../view/marketplace/MarketPlaceView"));
const OfferSellerDetails = lazy(() => import("../../view/marketplace/OfferSellerDetails"));
const OfferBuyerDetailsView = lazy(() => import("../../view/marketplace/OfferBuyerDetailsView"));
const OfferAdminDetailsView = lazy(() => import("../../view/marketplace/OfferAdminDetailsView"));
const CreateOfferView = lazy(() => import("../../view/marketplace/CreateOfferView"));
const OfferSellerRequestDocuments = lazy(() => import("../../view/marketplace/OfferSellerRequestDocuments"));
const PointsSharesView = lazy(() => import("../../view/points-shares/PointsSharesView"));
const BuyPointsView = lazy(() => import("../../view/points-shares/BuyPointsView"));
const SellPointsView = lazy(() => import("../../view/points-shares/SellPointsView"));
const PointsTransferView = lazy(() => import("../../view/points-shares/PointsTransferView"));
const PointsOfferCreateView = lazy(() => import("../../view/points-shares/PointsOfferCreateView"));
const PointsOfferCreateSuccessView = lazy(() => import("../../view/points-shares/PointsOfferCreateSuccessView"));
const PointsBuyBestOfferView = lazy(() => import("../../view/points-shares/PointsBuyBestOfferView"));
const PointsPurchaseSuccessView = lazy(() => import("../../view/points-shares/PointsPurchaseSuccessView"));
const PointsTransferCreateView = lazy(() => import("../../view/points-shares/PointsTransferCreateView"));
const PointsTransferSuccessView = lazy(() => import("../../view/points-shares/PointsTransferSuccessView"));
const BankDetailsView = lazy(() => import("../../view/points-shares/BankDetailsView"));
const CustomErrorView = lazy(() => import("../../view/error/CustomErrorView"));
const BookingView = lazy(() => import("../../view/booking/BookingView"));
const BookingConfirmationView = lazy(() => import("../../view/booking/BookingConfirmationView"));
const BookingReplaceView = lazy(() => import("../../view/booking/BookingReplaceView"));
const BookingReplaceConfirmationView = lazy(() => import("../../view/booking/BookingReplaceConfirmationView"));
const BookingUnconfirmedView = lazy(() => import("../../view/booking/BookingUnconfirmedView"));
const ReservationsView = lazy(() => import("../../view/reservations/ReservationsView"));
const WaitingListEditView = lazy(() => import("../../view/reservations/WaitingListEditView"));
const ReservationView = lazy(() => import("../../view/reservations/ReservationView"));
const PrivacyAndNotificationsView = lazy(() => import("../../view/profile/PrivacyAndNotificationsView"));
const MyBankAccountsView = lazy(() => import("../../view/profile/MyBankAccountsView"));
const AddNewBankAccountView = lazy(() => import("../../view/profile/AddNewBankAccountView"));
const BlogNews = lazy(() => import("../../view/articles/BlogNews"));
const ChangePasswordView = lazy(() => import("../../view/profile/ChangePasswordView"));
const PaymentsDocumentsView = lazy(() => import("../../view/payments-documents/PaymentsDocumentsView"));
const ArticlePage = lazy(() => import("../../view/articles/ArticlePage"));
const DirectDebitBankDetailsView = lazy(() => import("../../view/payments-documents/DirectDebitBankDetailsView"));
const InvoicePaymentSuccessView = lazy(() => import("../../view/payments-documents/InvoicePaymentSuccessView"));
const InvoicePaymentFailView = lazy(() => import("../../view/payments-documents/InvoicePaymentFailView"));
const BookingInformationSelectionView = lazy(() => import("../../view/booking-information/BookingInformationSelectionView"));
const BookingInformationCalendarView = lazy(() => import("../../view/booking-information/BookingInformationCalendarView"));
const BookingInformationPointsFreeWeeksView = lazy(() => import("../../view/booking-information/BookingInformationPointsFreeWeeksView"));
const DirectSalesOrderView = lazy(() => import("../../view/direct-sales-order/DirectSalesOrderView"));
const DirectSalesOrderSuccessView = lazy(() => import("../../view/direct-sales-order/DirectSalesOrderSuccessView"));
const BuySharesContract = lazy(() => import("../../dialog/membership/BuySharesContract"));
const AvailabilityOverviewSelectionView = lazy(() => import("../../view/availability-overview/AvailabilityOverviewSelectionView"));
const AvailabilityOverviewCompareView = lazy(() => import("../../view/availability-overview/AvailabilityOverviewCompareView"));

const useStyles = makeStyles({
    main: {
        minHeight: "100vh"
    }
});

const Main = ({displayIdleDialog}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {location, isRouteInLocation, navigate} = useLocation();
    const {keycloak, tokenParsed, logout, userId, authenticated, isAdmin, isEditorAdmin, register, isProspect, isTrialMember} = useKeycloakFunctions();
    const [newsletterDialogShown, setNewsletterDialogShown] = useLocalStorage(CONSTANTS.NEWSLETTER_DIALOG_SHOWN);
    const [cookies, setCookie, removeCookie] = useCookies([CONSTANTS.COOKIE_TRIAL_REGISTRATION.name, CONSTANTS.COOKIE_INDIRECT_REGISTRATION.name, CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.name]);
    const {pageTypes, pageTitles, sendLogin, sendCustomEvent, eventNames, loginTypes} = useAnalytics();

    const [localeState, localeDispatch] = useContext(LocaleContext);
    const {maintenanceDetails, isMaintenanceWindowOpen, loadingMaintenance} = useMaintenanceWindow(localeState?.localeUppercase);
    const [localizedState] = useContext(LocalizedContext);
    const [userState] = useContext(UserContext);
    const {isPrivateRoute} = useContext(RouteContext);

    const [storedLocale, setStoredLocale] = useState();
    const [showNewsleterDialog, setShowNewsleterDialog] = useState(false);
    const [displayMaintenancePage, setDisplayMaintenancePage] = useState(false);
    const [userRequestedSignOut, setUserRequestedSignOut] = useState();
    const [openInAppBrowserDialog, setOpenInAppBrowserDialog] = useState(false);
    const [disableOnlineAccessPath, setDisableOnlineAccessPath] = useState(true);
    const [disableAvailabilityOverview, setDisableAvailabilityOverview] = useState(true);
    const [disableDocumentsSection, setDisableDocumentsSection] = useState(false);
    const [, setSessionTimeoutHandler] = useState();

    const source = getQueryVariable("source");
    const returnUrl = getQueryVariable("returnUrl") || null;
    const isBooking = location?.pathname.replace("/" + localeState.locale + "/", "/").startsWith(ROUTES.BOOKING);

    const doLogout = (options) => {
        sendCustomEvent({
            event_name: eventNames.LOGOUT,
            login_status: "false"
        });

        if (options) {
            logout(options);
        } else {
            logout();
        }
    };

    const logoutUser = () => {
        const logoutURL = new URL(window.location.href);
        logoutURL.searchParams.set("displayIdleDialog", true);
        doLogout({redirectUri: logoutURL.href});
    };

    // Chat guuru initialization
    const initChatloader = () => {
        window.guuru = window.Guuru();
        window.guuru.init({
            appId: CHAT_GUURU_APP_ID,
            locale: localeState?.locale.slice(0, 2),
            user: {
                name: tokenParsed?.name,
                email: tokenParsed?.email
            },
            events: [
                {
                    id: "loader:initialized",
                    handler: () => {
                        const guuruChat = document.getElementById("GuuruMain");
                        if (guuruChat) {
                            guuruChat.style.zIndex = 1;
                        }
                    },
                },
            ],
        });

    };

    const isInAppBrowser = () => {
        return Boolean((navigator.userAgent || navigator.vendor || window.opera).match(new RegExp(`(${[
            "WebView",
            "(iPhone|iPod|iPad)(?!.*Safari/)",
            "Android.*(wv)",
        ].join("|")})`, "ig")));
    };

    useEffect(() => {
        if (isInAppBrowser() && source === "hapimag-mobile-app") {
            setOpenInAppBrowserDialog(true);
        }
    }, []);

    useEffect(() => {

        if (("Guuru" in window)) {
            initChatloader();
        } else {
            document.addEventListener(
                "guuru:loaded",
                () => initChatloader(),
                {once: true},
            );
        }

        return () => {
            if ("guuru" in window) {
                window.guuru.destroy();
            }
        };

    }, [localeState?.locale, tokenParsed]);

    useEffect(() => {
        checkMaintenancePage(userId).then(value => {
            setDisplayMaintenancePage(value);
        });

        disableURLForOnlineAccess().then(value => {
            setDisableOnlineAccessPath(value);
        });

        checkAvailabilityOverviewPageDisabled(userId).then(value => {
            setDisableAvailabilityOverview(value);
        });

        if (isProspect() || isTrialMember()) {
            checkDocumentsForTrialsAndProspectsDisabled(userId).then(value => {
                setDisableDocumentsSection(value);
            });
        }

        if (cookies[CONSTANTS.COOKIE_TRIAL_REGISTRATION.name]) {
            setCookie(CONSTANTS.COOKIE_TRIAL_REGISTRATION.name, false, CONSTANTS.COOKIE_TRIAL_REGISTRATION.options);
            removeCookie(CONSTANTS.COOKIE_TRIAL_REGISTRATION.name, CONSTANTS.COOKIE_TRIAL_REGISTRATION.options);
        }

        // If user has returned from an indirect registration and is NOT heading towards /imprint,
        // navigate to email verification view
        if (cookies[CONSTANTS.COOKIE_INDIRECT_REGISTRATION?.name] && !isRouteInLocation(ROUTES.IMPRINT)) {
            removeCookie(CONSTANTS.COOKIE_INDIRECT_REGISTRATION.name, CONSTANTS.COOKIE_INDIRECT_REGISTRATION.options);
            return navigate(ROUTES.EMAIL_VERIFICATION);
        }

        window.addEventListener("storage", (event) => {
            if (event?.key === "locale" && localeState.locale !== window.localStorage.getItem("locale")) {
                setStoredLocale(window.localStorage.getItem("locale"));
            } else if (getBrowser() === "Safari" && event?.key === CONSTANTS.USER_REQUESTED_SIGNOUT && window.localStorage.getItem(CONSTANTS.USER_REQUESTED_SIGNOUT) === "1") {
                setUserRequestedSignOut(true);
                window.localStorage.removeItem(CONSTANTS.USER_REQUESTED_SIGNOUT);
            }
        });
    }, []);

    /*
        We check if access token expired manually. This workaround eliminates the case
        where the user returns to app after it was idle on a mobile browser(chrome, safari-mobile).
    */
    useWindowFocus((focused) => {
        if (focused && keycloak?.authenticated) {
            if (new Date() > new Date(keycloak?.refreshTokenParsed?.exp * 1000)) {
                logoutUser();
            }
        }
    });

    keycloak.onReady = async (authenticated) => {
        if (authenticated) {
            if (await checkYearEndProcessing()) {
                doLogout();
            } else {
                // We refresh the access token manually. This is a workaround because we found that that
                // after logging in, if the access token is not refreshed at least once, when it expires
                // the first time it gets refreshed automatically by the keycloak-js lib
                keycloak.updateToken(KEYCLOAK_SESSION_EXPIRATION);
            }
        }
    };

    keycloak.onTokenExpired = () => {
        keycloak.updateToken();
    };

    /*
    *  Handling the change of language in different tab
    *   Steps:
    *   1. Catching event from locale change in local storage
    *   2. Updating the local state
    *   3. Dispatching the new locale and perform language change event
    */
    const changeLocale = (newLocale) => {
        const value = Object.keys(SUPPORTED_LANGUAGE).find(k => SUPPORTED_LANGUAGE[k] === newLocale);
        const cachedComposePages = getCachedData(ComposePagesQuery, contentful);
        localeDispatch({type: "SET_LOCALE", payload: {value, localizedState, composePages: cachedComposePages?.pageCollection?.items}});
    };

    // This useEffect handles session management by checking the expiration time of the Keycloak refresh token.
    // It calculates how much time remains before the token expires and sets a timeout to log out the user
    // automatically when the token expires. If the token has already expired, the user is logged out immediately.
    useEffect(() => {
        if (authenticated) {
            const timeLocal = new Date().getTime();
            const timeSkew = Math.floor(timeLocal / 1000) - keycloak.refreshTokenParsed?.iat;
            const expiresIn = (keycloak.refreshTokenParsed?.exp - (new Date().getTime() / 1000) + timeSkew) * 1000;

            if (expiresIn <= 0) {
                logoutUser();
            } else {
                setSessionTimeoutHandler(prev => {
                    if (prev) {
                        clearTimeout(prev);
                    }

                    return setTimeout(logoutUser, expiresIn);
                });
            }
        }
    }, [keycloak.refreshToken, authenticated]);

    useEffect(() => {
        if (storedLocale !== localeState.locale && !isBooking) {
            changeLocale(storedLocale);
        }
    }, [storedLocale]);

    useEffect(() => {
        if (!authenticated) {
            setNewsletterDialogShown(CONSTANTS.NEWSLETTER_DIALOG_SHOWN, false);
        } else {
            if ((getBrowser() === "Safari") && userRequestedSignOut) {
                doLogout();
            }
        }
    }, [authenticated, userRequestedSignOut, logout]);

    useEffect(() => {
        if (
            userState?.userSettings?.offers_by_email === null &&
            !isAdmin() &&
            !isEditorAdmin() &&
            !isRouteInLocation(ROUTES.ERROR_PAGE_404) &&
            !isRouteInLocation(ROUTES.ERROR_PAGE_504) &&
            !isRouteInLocation(ROUTES.ERROR_PAGE_500)
        ) {
            setShowNewsleterDialog(true);
        }
    }, [userState?.userSettings]);

    useEffect(() => {
        if (sendLogin && userId && userState?.userSettings?.member_id) {
            let loginData = {};

            if (cookies[CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.name]) {
                const loginType = {
                    login: {
                        login_type: loginTypes.MANUAL
                    }
                };
                loginData = {...loginData, ...loginType};

                setCookie(CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.name, false, CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.options);
                removeCookie(CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.name, CONSTANTS.COOKIE_LOGIN_BTN_CLICKED.options);
            }

            sendLogin(loginData);
        }
    }, [sendLogin]);

    return (
        <>
            {showNewsleterDialog && !newsletterDialogShown && authenticated && !returnUrl && !isRouteInLocation(ROUTES.EMAIL_VERIFICATION) && <NewsletterDialog />}
            {displayIdleDialog && <IdleDialog isPrivateRoute={isPrivateRoute} />}
            {!loadingMaintenance &&
                <div className={classes.main}>
                    <Suspense fallback={<div />}>
                        {isMaintenanceWindowOpen && displayMaintenancePage ?
                            (
                                <LocalizedSwitch>
                                    <Route
                                        exact
                                        path={ROUTES.MAINTENANCE}
                                    >
                                        <CustomErrorView
                                            title={maintenanceDetails?.title}
                                            message={maintenanceDetails?.text}
                                            type={CONSTANTS.ERROR_PAGE.MAINTENANCE_WINDOW}
                                            image={maintenanceDetails?.image?.url}
                                        />
                                    </Route>


                                    <Route path={"*"}>
                                        <LocalizedRedirect to={ROUTES.MAINTENANCE} />
                                    </Route>
                                </LocalizedSwitch>
                            ) : (
                                <LocalizedSwitch>
                                    <Route
                                        exact
                                        path={ROUTES.HOME}
                                        component={Home}
                                        type={pageTypes.HOME}
                                        seoTitle={`${i18n("view.availability_overview_view.seo_title")} | ${i18n("view.homepage.seo.title")}`}
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.REGISTRATION}
                                        render={() => {
                                            if (authenticated) {
                                                return <LocalizedRedirect to={ROUTES.HOME} />;
                                            } else {
                                                register(window.location.origin + ROUTES.HOME);
                                            }
                                        }}
                                        skipPageView
                                    />
                                    <Route
                                        exact
                                        path={ROUTES.TRIAL_REGISTRATION}
                                        render={(props) => {
                                            if (authenticated) {
                                                return <LocalizedRedirect to={ROUTES.HOME} />;
                                            } else {
                                                const queryParams = new URLSearchParams(props?.location.search);
                                                const redirectUri = queryParams?.get("redirectUri");

                                                // Set the trial cookie
                                                setCookie(CONSTANTS.COOKIE_TRIAL_REGISTRATION.name, true, CONSTANTS.COOKIE_TRIAL_REGISTRATION.options);
                                                register(redirectUri ?? window.location.origin + ROUTES.HOME);
                                            }
                                        }}
                                        skipPageView
                                    />
                                    {!disableOnlineAccessPath &&
                                        <Route
                                            exact
                                            path={ROUTES.ONLINE_ACCESS}
                                            component={OnlineAccessView}
                                        />
                                    }

                                    {/* <Route
                                        exact
                                        path={ROUTES.ONLINE_ACCESS_EMAIL_VERIFICATION}
                                        component={EmailVerificationOnlineAccess}
                                    /> */}

                                    <Route
                                        exact
                                        path={ROUTES.ADMIN_LOGIN}
                                        component={AdminDialog}
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.RESORTS}
                                        render={props => (
                                            <ResortsView
                                                {...props}
                                                slug={ROUTES.RESORTS}
                                            />
                                        )}
                                        skipPageView
                                    />

                                    <PrivateRoute
                                        path={ROUTES.BUTTONS}
                                        component={ButtonsAndChips}
                                        skipPageView
                                    />

                                    {/* i18n and L10n */}
                                    <PrivateRoute
                                        path={ROUTES.INTL}
                                        component={Intl}
                                        skipPageView
                                    />

                                    <PrivateRoute
                                        path={ROUTES.VERSIONS}
                                        component={Versions}
                                        skipPageView
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.IMPRINT}
                                        component={LegalPages}
                                    />

                                    <Route
                                        path={ROUTES.IMPRINT + "/:legal_page"}
                                        component={LegalPages}
                                        skipPageView
                                    />

                                    <PrivateRoute
                                        exact
                                        path={ROUTES.EMAIL_VERIFICATION}
                                        component={EmailVerificationView}
                                        seoTitle={`Hapimag | ${i18n("view.email_verification.success.seo.title")}`}
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.ARTICLES}
                                        render={props => (
                                            <BlogNews
                                                {...props}
                                                slug={ROUTES.ARTICLES}
                                            />
                                        )}
                                        seoTitle={i18n("view.blog_overview.seo.title")}
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.ARTICLES + "/:slug"}
                                        component={ArticlePage}
                                        skipPageView
                                    />

                                    <Route
                                        exact
                                        path={ROUTES.BECOME_MEMBER}
                                        component={BecomeMemberView}
                                    />

                                    <Route
                                        path={ROUTES.MARKET_PLACE}
                                        skipPageView
                                    >

                                        <LocalizedSwitch>

                                            <PrivateRoute
                                                roles={[SHARE_MARKETPLACE_USER]}
                                                path={ROUTES.MARKET_PLACE_CREATE_OFFER}
                                                component={CreateOfferView}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <PrivateRoute
                                                exact
                                                roles={[SHARE_MARKETPLACE_USER]}
                                                path={ROUTES.MARKET_PLACE_SELL_OFFER + "/:id/request-documents"}
                                                component={OfferSellerRequestDocuments}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <PrivateRoute
                                                exact
                                                roles={[SHARE_MARKETPLACE_USER]}
                                                path={ROUTES.MARKET_PLACE_SELL_OFFER + "/:id"}
                                                component={OfferSellerDetails}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <PrivateRoute
                                                exact
                                                roles={[SHARE_MARKETPLACE_USER]}
                                                path={ROUTES.MARKET_PLACE_BUY_OFFER + "/:id"}
                                                component={OfferBuyerDetailsView}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <PrivateRoute
                                                exact
                                                path={ROUTES.MARKET_PLACE_ADMIN_OFFER + "/:id"}
                                                component={OfferAdminDetailsView}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <PrivateRoute
                                                exact={false}
                                                roles={[SHARE_MARKETPLACE_USER]}
                                                path={ROUTES.MARKET_PLACE_BUY}
                                                component={MarketPlaceView}
                                                redirectPath={ROUTES.UNDER_CONSTRUCTION}
                                            />

                                            <Route path={"*"}>
                                                <Redirect to={ROUTES.ERROR_PAGE_404} />
                                            </Route>

                                        </LocalizedSwitch>

                                    </Route>

                                    <Route
                                        path={ROUTES.POINTS_SHARES}
                                        skipPageView
                                    >

                                        <LocalizedSwitch>

                                            <PrivateRoute
                                                exact
                                                path={ROUTES.POINTS_SHARES_POINTS}
                                                component={PointsSharesView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.shares_points.seo.title")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_SHARES}
                                                component={PointsSharesView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.shares_points.seo.title.shares_overview")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_BUY}
                                                component={BuyPointsView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.seo.title.points_platform")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_SELL}
                                                component={SellPointsView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.points_shares.points.seo.title.sellable_points_overview")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_BANK_DETAILS}
                                                component={BankDetailsView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.seo.title.bank_details")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_SELL_CREATE}
                                                component={PointsOfferCreateView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.points_shares.points.seo.title.create_points_offer")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_SELL_SUCCESS}
                                                component={PointsOfferCreateSuccessView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.points_shares.points.seo.title.points_offer_created_confirmation")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_BUY_OFFER}
                                                component={PointsBuyBestOfferView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.buy_points.seo.title")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_BUY_SUCCESS}
                                                component={PointsPurchaseSuccessView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.points_shares.points.seo.title.points_purchase_confirmation")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_TRANSFER}
                                                component={PointsTransferView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.sell_points.seo.title.transferable_points_overview")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_TRANSFER_CREATE}
                                                component={PointsTransferCreateView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.sell_points.seo.title.transfer_points_form")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.POINTS_SHARES_POINTS_TRANSFER_SUCCESS}
                                                component={PointsTransferSuccessView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.profile.points_shares.sell_points.seo.title.points_transfered_confirmation")}`}
                                            />

                                            <Route path={"*"}>
                                                <Redirect to={ROUTES.ERROR_PAGE_404} />
                                            </Route>

                                        </LocalizedSwitch>

                                    </Route>

                                    {!disableDocumentsSection &&
                                    <Route
                                        path={ROUTES.PAYMENTS_DOCUMENTS}
                                        skipPageView
                                    >

                                        <LocalizedSwitch>

                                            <PrivateRoute
                                                exact
                                                path={ROUTES.USER_DOCUMENTS}
                                                component={PaymentsDocumentsView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.payments_documents.seo.title.documents")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.INVOICES_CREDITS}
                                                roles={[[CUSTOMER], [CUSTOMER_MEMBER], [CUSTOMER_EMPLOYEE]]}
                                                component={PaymentsDocumentsView}
                                                seoTitle={`${i18n("common.label.my_hapimag")} | ${i18n("view.payments_documents.seo.title.invoices")}`}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.INVOICE_PAYMENT_SUCCESS}
                                                component={InvoicePaymentSuccessView}
                                            />

                                            <PrivateRoute
                                                path={ROUTES.INVOICE_PAYMENT_FAIL}
                                                component={InvoicePaymentFailView}
                                            />

                                            <PrivateRoute
                                                exact
                                                path={ROUTES.INVOICES_CREDITS_DIRECT_DEBIT_BANK_DETAILS}
                                                component={DirectDebitBankDetailsView}
                                            />

                                            <Route path={"*"}>
                                                <Redirect to={ROUTES.ERROR_PAGE_404} />
                                            </Route>

                                        </LocalizedSwitch>

                                    </Route>
                                    }

                                    <PrivateRoute
                                        roles={[[CUSTOMER_MEMBER], [CUSTOMER_TRIAL], [CUSTOMER], [CUSTOMER_EMPLOYEE]]}
                                        path={ROUTES.BOOKING}
                                        component={BookingView}
                                        seoTitle={i18n("view.booking.confirmation_success.seo.title")}
                                    />

                                    <PrivateRoute
                                        roles={[[CUSTOMER_MEMBER], [CUSTOMER_TRIAL], [CUSTOMER], [CUSTOMER_EMPLOYEE]]}
                                        path={ROUTES.BOOKING_CONFIRMATION}
                                        component={BookingConfirmationView}
                                    />

                                    <PrivateRoute
                                        roles={[[CUSTOMER_MEMBER], [CUSTOMER_TRIAL], [CUSTOMER], [CUSTOMER_EMPLOYEE]]}
                                        path={ROUTES.BOOKING_REPLACE}
                                        component={BookingReplaceView}
                                    />

                                    <PrivateRoute
                                        roles={[[CUSTOMER_MEMBER], [CUSTOMER_TRIAL], [CUSTOMER], [CUSTOMER_EMPLOYEE]]}
                                        path={ROUTES.BOOKING_REPLACE_CONFIRMATION}
                                        component={BookingReplaceConfirmationView}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.BOOKING_UNCONFIRMED}
                                        component={BookingUnconfirmedView}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_UPCOMING}
                                        component={ReservationsView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                        seoTitle={`${i18n("view.reservations.seo.title")} | ${i18n("view.reservations.seo.title.upcoming_holidays")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_PAST}
                                        component={ReservationsView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                        seoTitle={`${i18n("view.reservations.seo.title")} | ${i18n("view.reservations.seo.title.past_holidays")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_CANCELLED}
                                        component={ReservationsView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                        seoTitle={`${i18n("view.reservations.seo.title")} | ${i18n("view.reservations.seo.title.cancelled_holidays")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_WAITING_LIST}
                                        component={ReservationsView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                        seoTitle={`${i18n("view.reservations.seo.title")} | ${i18n("view.reservations.seo.title.waiting_list")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_WAITING_LIST_EDIT + "/:id"}
                                        component={WaitingListEditView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                        seoTitle={`${i18n("view.reservations.seo.title")} | ${i18n("view.reservations.seo.title.waiting_list")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATION_DETAILS}
                                        component={ReservationView}
                                        skipPageView
                                    />

                                    <PrivateRoute
                                        path={ROUTES.RESERVATIONS_MANAGE}
                                        component={ReservationsView}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.PROFILE_ACCOUNT_SETTINGS}
                                        component={AccountSettingsView}
                                        seoTitle={`Hapimag | ${i18n("view.profile.account_settings.seo.title")}`}
                                    />

                                    <PrivateRoute
                                        path={PROFILE_ACCOUNT_SETTINGS_PERSONAL_DETAILS}
                                        component={PersonalDetailsView}
                                        seoTitle={`Hapimag | ${i18n("view.profile.account_settings.seo.title.personal_details")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.PROFILE_ACCOUNT_SETTINGS_PRIVACY_AND_NOTIFICATIONS}
                                        component={PrivacyAndNotificationsView}
                                        seoTitle={`Hapimag | ${i18n("view.profile.account_settings.seo.title.notification_settings")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.PROFILE_ACCOUNT_SETTINGS_CHANGE_PASSWORD}
                                        component={ChangePasswordView}
                                        seoTitle={`Hapimag | ${i18n("view.profile.account_settings.seo.title.change_password")}`}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.PROFILE_ACCOUNT_SETTINGS_MY_BANK_ACCOUNTS}
                                        component={MyBankAccountsView}
                                        roles={[[CUSTOMER], [CUSTOMER_MEMBER], [CUSTOMER_EMPLOYEE]]}
                                        redirectPath={ROUTES.ERROR_PAGE_404}
                                    />

                                    <PrivateRoute
                                        path={ROUTES.PROFILE_ACCOUNT_SETTINGS_ADD_BANK_ACCOUNT}
                                        component={AddNewBankAccountView}
                                    />

                                    <Route
                                        path={ROUTES.RESORTS_BOOKING_INFORMATION}
                                        skipPageView
                                    >
                                        <LocalizedSwitch>
                                            <Route
                                                exact
                                                path={ROUTES.RESORTS_BOOKING_INFORMATION}
                                                component={BookingInformationSelectionView}
                                                type={pageTypes.BOOKING_INFORMATION}
                                                title={pageTitles.BOOKING_INFORMATION}
                                                seoTitle={`Hapimag | ${i18n("layout.footer.booking_info")}`}
                                            />

                                            <Route
                                                path={ROUTES.RESORTS_BOOKING_INFORMATION + "/:resort_id"}
                                                component={BookingInformationCalendarView}
                                                skipPageView
                                            />
                                        </LocalizedSwitch>
                                    </Route>

                                    <Route
                                        path={ROUTES.POINTS_FREE_WEEKS}
                                        skipPageView
                                    >
                                        <LocalizedSwitch>
                                            <Route
                                                exact
                                                path={ROUTES.POINTS_FREE_WEEKS}
                                                component={BookingInformationPointsFreeWeeksView}
                                                type={pageTypes.BOOKING_INFORMATION}
                                                title={pageTitles.BOOKING_INFORMATION}
                                            />
                                        </LocalizedSwitch>
                                    </Route>

                                    <PrivateRoute
                                        component={DirectSalesOrderView}
                                        path={ROUTES.DIRECT_SALES_ORDER}
                                    />

                                    <Route
                                        path={ROUTES.DIRECT_SALES_ORDER}
                                        skipPageView
                                    >
                                        <LocalizedSwitch>
                                            <PrivateRoute
                                                exact
                                                path={ROUTES.DIRECT_SALES_ORDER}
                                                component={DirectSalesOrderView}
                                            />

                                            <PrivateRoute
                                                exact
                                                path={ROUTES.DIRECT_SALES_ORDER_SUCCESS}
                                                component={DirectSalesOrderSuccessView}
                                            />
                                        </LocalizedSwitch>
                                    </Route>
                                    <PrivateRoute
                                        path={ROUTES.BUY_SHARES_CONTRACT}
                                        component={BuySharesContract}
                                    />

                                    {/* Availability Overview */}
                                    {!disableAvailabilityOverview &&
                                        <Route
                                            path={ROUTES.AVAILABILITY_OVERVIEW}
                                            skipPageView
                                        >
                                            <LocalizedSwitch>
                                                <Route
                                                    exact
                                                    path={ROUTES.AVAILABILITY_OVERVIEW}
                                                    component={AvailabilityOverviewSelectionView}
                                                    seoTitle={`${i18n("view.availability_overview_view.seo_title")} - ${i18n("view.availability_overview_view.availability_overview")}`}
                                                    skipPageView
                                                />

                                                <Route
                                                    path={ROUTES.AVAILABILITY_OVERVIEW + "/:resort_id"}
                                                    render={() => (
                                                        <ApartmentCategoryAvailabilityCalendarProvider>
                                                            <AvailabilityOverviewProvider>
                                                                <AvailabilityOverviewCompareView />
                                                            </AvailabilityOverviewProvider>
                                                        </ApartmentCategoryAvailabilityCalendarProvider>
                                                    )}
                                                    skipPageView
                                                />
                                            </LocalizedSwitch>
                                        </Route>
                                    }

                                    {/*ERROR PAGES*/}
                                    <Route path={ROUTES.ERROR_PAGE_504}>
                                        <CustomErrorView
                                            title={i18n("view.error_page.error_504_delay")}
                                            message={i18n("view.error_page.error_504_label")}
                                            type={CONSTANTS.ERROR_PAGE[504]}
                                        />
                                    </Route>

                                    <Route path={ROUTES.ERROR_PAGE_500}>
                                        <CustomErrorView
                                            title={i18n("view.error_page.error_500_title")}
                                            buttonLabel={i18n("common.label.refresh")}
                                            message={i18n("view.error_page.error_500_label")}
                                            type={CONSTANTS.ERROR_PAGE[500]}
                                        />
                                    </Route>

                                    <Route
                                        exact
                                        path={ROUTES.UNDER_CONSTRUCTION}
                                    >
                                        <CustomErrorView
                                            title={i18n("view.error_page.under_construction_title")}
                                            message={i18n("view.error_page.under_construction_label")}
                                            buttonLabel={i18n("view.auth.link.back_home")}
                                            type={CONSTANTS.ERROR_PAGE.UNDER_CONSTRUCTION}
                                        />
                                    </Route>

                                    {/* Handle all dynamic paths of the app (Resort/Compose pages) */}
                                    <Route
                                        path={"*"}
                                        skipPageView
                                    >
                                        {({match}) => {
                                            return (
                                                <DynamicRouter match={match} />
                                            );
                                        }}
                                    </Route>
                                </LocalizedSwitch>
                            )
                        }
                    </Suspense>
                </div>
            }

            {openInAppBrowserDialog && (
                <InAppBrowserDialog
                    isOpen={openInAppBrowserDialog}
                    handleClose={() => setOpenInAppBrowserDialog(false)}
                />
            )}
        </>
    );
};

Main.defaultProps = {
    displayIdleDialog: false,
};

Main.propTypes = {
    displayIdleDialog: PropTypes.bool
};

export default Main;
